import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {WarningCircle, Users, Info} from '../../icons';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {ERROR_AD_ACCOUNT_NOT_LINKED_TO_AGENCY_CLIENT} from '../../../settings/settings';
import {LocalStorage} from '../../../utils/LocalStorage';
import {fetchCustomMetrics} from '../../../hooks/useCustomMetrics';
import {setCustomMetricLabelsCache} from '../../dashboard/columns';
import {Spinner} from '../../Spinner';
import {YesNoModal} from '../../elements/YesNoModal';
import {useFicha21} from '../../../context/UserModulesContext';
import {UncontrolledSummaryCard} from '../../alerts_center/uncontrolled/UncontrolledSummaryCard';
import {anchorAttr} from '../../../guides/anchors';
import {ImportEconomicsDialog} from '../../client/economia/ImportEconomicsDialog';
import {ClientV2Row} from './ClientV2Row';
import {
    ClientsV2Toolbar,
    SCOPE_MINE,
    SCOPE_AGENCY,
    ORDER_ATTENTION,
    ORDER_NAME,
    ORDER_SPEND,
    ORDER_GOALS_AT_RISK,
    ORDER_ACTIVITY,
    VIEW_ECONOMICS,
    VIEW_SUMMARY,
} from './ClientsV2Toolbar';
import {ClientsEconomicsHeader, economicsStackedLabels} from './economics/ClientsEconomicsHeader';
import {economicsAttentionRank, loadClientsView, saveClientsView} from './economics/ClientEconomicsRow';
import {activityTotal} from './Sparkline';
import {
    SEVERITY_GROUPS,
    groupHelp,
    monthElapsedFraction,
    rowSeverity,
    attentionScore,
    severityRank,
    hasGoalsPace,
    goalsBehind,
} from './clientsV2Thresholds';

const GROUP_LABEL_KEY = {
    red: 'clients_v2.group_red',
    gold: 'clients_v2.group_gold',
    green: 'clients_v2.group_green',
    gray: 'clients_v2.group_gray',
};

/**
 * ¿Este cliente entra en el alcance elegido? En "Agencia" entran todos; en "Míos", solo los
 * asignados al usuario.
 *
 * Vive suelta porque la consultan dos cálculos distintos —la lista visible y el contador del filtro
 * de inactivos— y si se separan, el botón dice un número que la lista no muestra.
 */
function matchesScope(client, scope) {
    return scope !== SCOPE_MINE || !!client.assigned_to_me;
}

/**
 * Listado de Clientes de la UI v2 (UX 2.1).
 *
 * Una fila por cliente con el estado de la operación del mes: inversión contra presupuesto (con la
 * marca del ritmo esperado), objetivos en ritmo, cuentas conectadas, actividad de la bitácora y
 * alertas / media plan pendiente. El semáforo del borde izquierdo y el orden
 * "Atención primero" salen de `clientsV2Thresholds`, que concentra los umbrales para calibrarlos.
 *
 * Todo el cálculo es client-side sobre un único GET a `agency/clients/overview/`.
 */
export function AgencyClientsV2Content(props) {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [payload, setPayload] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);

    const [scope, setScope] = useState(SCOPE_MINE);
    const [scopeTouched, setScopeTouched] = useState(false);
    const [search, setSearch] = useState('');
    const [order, setOrder] = useState(ORDER_ATTENTION);
    const [showInactive, setShowInactive] = useState(false);
    // Vista «Economía» (módulo Economía del cliente). Sin el módulo no hay otra vista: aunque el
    // estado —o lo recordado— dijera otra cosa, se dibuja el Resumen. El módulo se mira en los DOS
    // lados: el flag de la sesión y el `economics_enabled` del MISMO payload que trae los datos, así
    // el selector y las columnas no pueden salir de respuestas distintas (R-ecofe-30).
    const {clientEconomicsEnabled} = useFicha21();
    const [viewChoice, setViewChoice] = useState(() => loadClientsView(LocalStorage.fetchUserId()));
    const economicsAvailable = clientEconomicsEnabled && !!payload && payload.economics_enabled === true;
    const view = economicsAvailable ? viewChoice : VIEW_SUMMARY;
    const changeView = useCallback((next) => {
        setViewChoice(next);
        saveClientsView(LocalStorage.fetchUserId(), next);
    }, []);
    const [importOpen, setImportOpen] = useState(false);
    // Acción de fila esperando confirmación: {type: 'deactivate' | 'delete' | 'activate_link', client}.
    const [confirmAction, setConfirmAction] = useState(null);
    // El tooltip de objetivos muestra el nombre de la métrica de cada goal, y una métrica
    // personalizada llega como "CUSTOM:<id>": sin el cache de display_names se ve ese crudo en
    // pantalla. Mismo patrón que ControlCentralContent. El valor del flag no se lee (el cache lo
    // consulta `getMetricName` en tiempo de render): alcanza con que el set dispare el repintado.
    const [, setCustomMetricLabelsReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchCustomMetrics().then((items) => {
            if (cancelled) return;
            setCustomMetricLabelsCache(items);
            setCustomMetricLabelsReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoaded(false);
        setHasError(false);

        const url = `${API_URL}agency/clients/overview/?tz_offset=${new Date().getTimezoneOffset()}`;
        new APIClient(url).call('GET').then((data) => {
            if (cancelled) return;
            // OKResponse del backend envuelve el payload: {status: 'OK', data: {...}}.
            if (data && data.status === 'OK' && data.data) {
                setPayload(data.data);
            } else {
                // Estado de error genérico: nunca exponemos el `error_message` del backend, que
                // puede filtrar información de otra agencia (aislamiento cross-agency).
                setHasError(true);
            }
            setLoaded(true);
        });

        return () => {
            cancelled = true;
        };
    }, [reloadToken]);

    const canViewAgencyScope = payload ? payload.can_view_agency_scope === true : false;

    // Si el usuario no tiene clientes asignados, arrancar en "Míos" sería una pantalla vacía sin
    // motivo: mientras no toque el segmented, lo mandamos al alcance de la agencia.
    useEffect(() => {
        if (!payload || scopeTouched) return;
        if (Number(payload.mine_count || 0) === 0 && payload.can_view_agency_scope === true) {
            setScope(SCOPE_AGENCY);
        }
    }, [payload, scopeTouched]);

    const handleScopeChange = useCallback((next) => {
        setScopeTouched(true);
        setScope(next);
    }, []);

    const handleOpenClient = useCallback((path) => navigate(path), [navigate]);

    const refetch = useCallback(() => setReloadToken((x) => x + 1), []);

    // Mismo endpoint que la pantalla clásica: `PUT client/{id}/activate|deactivate`. Al activar, el
    // backend puede pedir confirmación para vincular las cuentas del cliente (add_ada_to_cli_if_none).
    const callStatusChange = useCallback((client, action, addAdAccounts) => {
        new APIClient(`${API_URL}client/${client.id}/${action}`)
            .call('PUT', {'add_ada_to_cli_if_none': addAdAccounts})
            .then((data) => {
                if (action === 'activate' && data && data.status === ERROR_AD_ACCOUNT_NOT_LINKED_TO_AGENCY_CLIENT) {
                    setConfirmAction({type: 'activate_link', client});
                    return;
                }
                if (data && data.status === 'ERROR') {
                    // Mensaje genérico a propósito (aislamiento cross-agency).
                    toast.error(t('clients_v2.action_error'));
                }
                refetch();
            });
    }, [refetch, t]);

    const handleRequestDeactivate = useCallback((client) => setConfirmAction({type: 'deactivate', client}), []);
    const handleRequestDelete = useCallback((client) => setConfirmAction({type: 'delete', client}), []);
    const handleActivate = useCallback((client) => callStatusChange(client, 'activate', false), [callStatusChange]);

    const handleConfirmYes = useCallback(() => {
        const action = confirmAction;
        setConfirmAction(null);
        if (!action) return;
        if (action.type === 'deactivate') {
            callStatusChange(action.client, 'deactivate', false);
        } else if (action.type === 'activate_link') {
            callStatusChange(action.client, 'activate', true);
        } else if (action.type === 'delete') {
            new APIClient(`${API_URL}client/${action.client.id}/delete`).call('DELETE').then((data) => {
                if (data && data.status === 'ERROR') {
                    toast.error(t('clients_v2.action_error'));
                }
                refetch();
            });
        }
    }, [confirmAction, callStatusChange, refetch, t]);

    const handleClientAdded = useCallback((newClientId, newClientName) => {
        if (props.afterSuccessfulAgencyClientSubmit) {
            props.afterSuccessfulAgencyClientSubmit(newClientId, newClientName);
            return;
        }
        navigate(`/client/${newClientId}/`);
    }, [navigate, props]);

    const elapsedFraction = useMemo(() => monthElapsedFraction(), []);

    // Severidad + score de atención + total de actividad, una sola vez por payload.
    const decorated = useMemo(() => {
        const clients = (payload && payload.clients) || [];
        return clients.map((client) => {
            const severity = rowSeverity(client);
            return {
                client,
                severity,
                score: attentionScore(client, severity),
                activity: activityTotal(client.activity_14d),
            };
        });
    }, [payload]);

    // Scope + búsqueda. Los inactivos se filtran después, para poder contarlos.
    const inScope = useMemo(() => {
        const query = search.trim().toLowerCase();
        return decorated.filter(({client}) => {
            if (!matchesScope(client, scope)) return false;
            if (query && !String(client.name || '').toLowerCase().includes(query)) return false;
            return true;
        });
    }, [decorated, scope, search]);

    // Inactivos del alcance actual, IGNORANDO la búsqueda a propósito: el contador del filtro es
    // "cuántos inactivos hay en esta lista", no "cuántos matchean lo que estoy tipeando" — si
    // dependiera del texto, el número (y el estado habilitado del botón) cambiarían tecla a tecla.
    const inactiveInScope = useMemo(
        () => decorated.filter(({client}) => matchesScope(client, scope) && !client.active).length,
        [decorated, scope],
    );

    // Si la agencia no tiene ningún cliente inactivo, el filtro no existe para nadie.
    const hasInactiveClients = useMemo(() => decorated.some(({client}) => !client.active), [decorated]);

    const visible = useMemo(() => {
        const rows = showInactive ? inScope.slice() : inScope.filter(({client}) => client.active);
        const byName = (a, b) => String(a.client.name || '').localeCompare(String(b.client.name || ''), 'es');

        if (order === ORDER_NAME) return rows.sort(byName);
        if (order === ORDER_SPEND) {
            // Un cliente cuyo gasto no se pudo expresar en una sola moneda (`total_spend: null`) NO
            // es un cliente que gastó poco: es uno del que no sabemos el total. Con `|| 0` competía
            // como si fuera cero y se hundía al fondo mezclado con los que de verdad no gastaron —
            // justo el que puede haber invertido millones. Van juntos al final, como bloque
            // identificable y ordenado por nombre, en vez de salpicados por un cast accidental.
            const unknown = ({client}) => (client.spend_unavailable ? 1 : 0);
            return rows.sort((a, b) => (unknown(a) - unknown(b))
                || (Number(b.client.total_spend || 0) - Number(a.client.total_spend || 0))
                || byName(a, b));
        }
        if (order === ORDER_GOALS_AT_RISK) {
            // Un objetivo que todavía no tiene evaluación no está «en riesgo» (ver `goalsBehind`).
            const gap = ({client}) => (hasGoalsPace(client) ? goalsBehind(client) : -1);
            return rows.sort((a, b) => (gap(b) - gap(a)) || byName(a, b));
        }
        if (order === ORDER_ACTIVITY) {
            return rows.sort((a, b) => (b.activity - a.activity) || byName(a, b));
        }
        // En la vista Economía, «Atención primero» es la atención de la ECONOMÍA: Revisar → Sin cargar
        // → Al día. La severidad operativa es un criterio que esa vista no muestra.
        if (view === VIEW_ECONOMICS) {
            return rows.sort((a, b) => (economicsAttentionRank(a.client) - economicsAttentionRank(b.client))
                || byName(a, b));
        }
        // Atención primero: primero el tramo de severidad, después el score, después el nombre.
        return rows.sort((a, b) => (severityRank(a.severity) - severityRank(b.severity))
            || (b.score - a.score)
            || byName(a, b));
    }, [inScope, order, showInactive, view]);

    // La fila que explica el recorrido de Clientes con cartera: la PRIMERA visible con líneas (sin
    // líneas la fila es «Configurar» y no tiene las celdas). Sólo en el Resumen: la vista Economía
    // dibuja otras celdas.
    const guideRowClientId = useMemo(() => {
        if (view !== VIEW_SUMMARY) return null;
        const first = visible.find(({client}) => Number(client.business_units_count || 0) > 0);
        return first ? first.client.id : null;
    }, [visible, view]);

    const groupCounts = useMemo(() => {
        const counts = {};
        SEVERITY_GROUPS.forEach((group) => { counts[group] = 0; });
        visible.forEach(({severity}) => { counts[severity] = (counts[severity] || 0) + 1; });
        return counts;
    }, [visible]);

    const currentUserId = LocalStorage.fetchUserId();

    const renderHeaderCell = (labelKey, tipKey, extraClass = '', guideAnchor = {}) => (
        <div className={`cv2-head__cell ${extraClass}`} {...guideAnchor}>
            {tipKey ? (
                <span className="cv2-tip">
                    <span className="cv2-tip__label">{t(labelKey)}</span>
                    <span className="warning-tooltip -below">
                        <span className="warning-tooltip-body">
                            <span className="warning-tooltip-text">{t(tipKey)}</span>
                        </span>
                    </span>
                </span>
            ) : t(labelKey)}
        </div>
    );

    const renderList = () => {
        if (!visible.length) {
            const searching = search.trim().length > 0;
            return (
                <div className="cv2-empty">
                    {searching ? <WarningCircle size={34}/> : <Users size={34}/>}
                    <h3>{searching ? t('clients_v2.empty_search_title') : t('clients_v2.empty_title')}</h3>
                    <p>{searching ? t('clients_v2.empty_search_text') : t('clients_v2.empty_text')}</p>
                </div>
            );
        }

        // Los grupos de severidad son de la operación del mes (presupuesto, objetivos, alertas): en
        // la vista Economía no dicen nada de lo que se está mirando, así que la lista va corrida.
        const grouped = order === ORDER_ATTENTION && view === VIEW_SUMMARY;
        let lastSeverity = null;

        return (
            <div className="cv2-list">
                {visible.map(({client, severity}) => {
                    const rows = [];
                    if (grouped && severity !== lastSeverity) {
                        // El recorrido de primera visita apunta al PRIMER grupo (el de arriba).
                        const guideAnchor = lastSeverity === null ? anchorAttr('clients_list_groups') : {};
                        lastSeverity = severity;
                        rows.push(
                            <div className="cv2-group" key={`group-${severity}`} {...guideAnchor}>
                                <span className={`cv2-dot -${severity}`}/>
                                <span className="cv2-tip cv2-group__tip" tabIndex={0}
                                      aria-describedby={`cv2-group-help-${severity}`}>
                                    <span className="cv2-group__label cv2-tip__label">{t(GROUP_LABEL_KEY[severity])}</span>
                                    <Info size={13} weight="bold" className="cv2-group__info" aria-hidden="true"/>
                                    <span className="warning-tooltip -below-left">
                                        <span className="warning-tooltip-body">
                                            <span className="warning-tooltip-text" id={`cv2-group-help-${severity}`}
                                                  data-testid={`cv2-group-help-${severity}`}>
                                                {groupHelp(t, severity)}
                                            </span>
                                        </span>
                                    </span>
                                </span>
                                <span className="cv2-group__count">{groupCounts[severity]}</span>
                                <span className="cv2-group__line"/>
                            </div>,
                        );
                    }
                    rows.push(
                        <ClientV2Row
                            key={client.id}
                            client={client}
                            severity={severity}
                            elapsedFraction={elapsedFraction}
                            currentUserId={currentUserId}
                            memoryEnabled={payload.memory_enabled === true}
                            mediaPlanEnabled={payload.media_plan_enabled === true}
                            onOpenClient={handleOpenClient}
                            onDeactivate={handleRequestDeactivate}
                            onActivate={handleActivate}
                            onDelete={handleRequestDelete}
                            view={view}
                            guideRow={client.id === guideRowClientId}
                        />,
                    );
                    return rows;
                })}
            </div>
        );
    };

    const renderBody = () => {
        if (!loaded) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }

        if (hasError || !payload) {
            return (
                <div className="cv2-empty">
                    <WarningCircle size={34}/>
                    <h3>{t('clients_v2.error_title')}</h3>
                    <p>{t('clients_v2.error_text')}</p>
                    <button type="button" className="cv2-btn cv2-btn--primary" onClick={() => setReloadToken(x => x + 1)}>
                        {t('clients_v2.error_retry')}
                    </button>
                </div>
            );
        }

        return (
            <>
                <ClientsV2Toolbar
                    scope={scope}
                    onScopeChange={handleScopeChange}
                    canViewAgencyScope={canViewAgencyScope}
                    mineCount={payload.mine_count || 0}
                    agencyCount={payload.agency_count || 0}
                    search={search}
                    onSearchChange={setSearch}
                    order={order}
                    onOrderChange={setOrder}
                    showInactive={showInactive}
                    inactiveCount={inactiveInScope}
                    hasInactiveClients={hasInactiveClients}
                    onToggleInactive={() => setShowInactive((current) => !current)}
                    onClientAdded={handleClientAdded}
                    economicsEnabled={economicsAvailable}
                    view={view}
                    onViewChange={changeView}
                    onImport={() => setImportOpen(true)}
                />

                {/* «Inversión sin control»: la tarjeta se protege sola (permiso propio de la solapa, toda la
                    2.1 desde UD-5) y sin plata en juego no se dibuja. */}
                <UncontrolledSummaryCard className="-list"/>

                {view === VIEW_ECONOMICS ? <ClientsEconomicsHeader/> : (
                <div className="cv2-head cv2-grid">
                    {/* La primera columna va sin título: el nombre del cliente se identifica solo y
                        el label competía con él, que está justo debajo. La celda igual existe —si se
                        elimina, las seis siguientes se corren una posición y ningún título cae sobre
                        su dato. */}
                    <div className="cv2-head__cell"/>
                    {renderHeaderCell('clients_v2.header_spend', 'clients_v2.header_spend_tip', '',
                        anchorAttr('clients_list_budget'))}
                    {renderHeaderCell('clients_v2.header_goals', 'clients_v2.header_goals_tip', 'cv2-col-goals')}
                    {renderHeaderCell('clients_v2.header_accounts', null, 'cv2-col-accounts')}
                    {renderHeaderCell('clients_v2.header_activity', 'clients_v2.header_activity_tip', 'cv2-col-activity')}
                    {renderHeaderCell('clients_v2.header_status', 'clients_v2.header_status_tip', '',
                        anchorAttr('clients_list_status'))}
                    <div className="cv2-head__cell"/>
                </div>
                )}

                {renderList()}

                {renderConfirmModal()}

                {economicsAvailable && (
                    <ImportEconomicsDialog isOpen={importOpen} onClose={() => setImportOpen(false)}
                                           onApplied={refetch}/>
                )}
            </>
        );
    };

    // Confirmaciones de las acciones de fila. Activar no confirma (es re-encender monitoreo);
    // el caso 'activate_link' es el backend pidiendo permiso para vincular las cuentas del cliente.
    const renderConfirmModal = () => {
        if (!confirmAction) return null;
        const name = confirmAction.client.name;
        const byType = {
            deactivate: {
                title: t('clients_v2.deactivate_confirm_title'),
                message: t('clients_v2.deactivate_confirm_message'),
                question: t('clients_v2.deactivate_confirm_question', {name}),
            },
            delete: {
                title: t('clients_v2.delete_confirm_title'),
                message: t('clients_v2.delete_confirm_message'),
                question: t('clients_v2.delete_confirm_question', {name}),
            },
            activate_link: {
                title: t('clients.link_ad_accounts_title'),
                message: t('clients.link_ad_accounts_message'),
                question: t('clients.link_ad_accounts_question'),
            },
        };
        const copy = byType[confirmAction.type];
        return (
            <YesNoModal
                isOpen={true}
                skipRenderButton={true}
                title={copy.title}
                message={copy.message}
                question={copy.question}
                onYesAnswer={handleConfirmYes}
                onNoAnswer={() => setConfirmAction(null)}
            />
        );
    };

    // Rótulos de cada dato cuando la fila se apila y el encabezado de columnas desaparece. Son los
    // MISMOS textos del encabezado, así que no pueden divergir; el CSS los lee como `content` (por
    // eso van entre comillas: una custom property usada en `content` tiene que ser un string CSS).
    const stackedLabels = {
        '--cv2-label-spend': JSON.stringify(t('clients_v2.header_spend')),
        '--cv2-label-goals': JSON.stringify(t('clients_v2.header_goals')),
        '--cv2-label-accounts': JSON.stringify(t('clients_v2.header_accounts')),
        '--cv2-label-activity': JSON.stringify(t('clients_v2.header_activity')),
        '--cv2-label-status': JSON.stringify(t('clients_v2.header_status')),
        ...(view === VIEW_ECONOMICS ? economicsStackedLabels(t) : {}),
    };

    return (
        <section className="section-content">
            {/* `-eco` cambia la plantilla de la grilla (encabezado y filas) y sus cortes. */}
            <div className={`cv2-screen${view === VIEW_ECONOMICS ? ' -eco' : ''}`} style={stackedLabels}>
                {renderBody()}
            </div>
        </section>
    );
}

export default AgencyClientsV2Content;
